import React, { useState } from 'react';
import { useTheme } from 'styled-components';

import {
  Button,
  Divider,
  Flex,
  Tag,
  Typography,
} from '../../../designComponents';
import { InfoRowStyled } from '../../../style/reusableStyle';
import { ClientReviewType, ReviewType } from '../../../types';
import { humanReadableDateWithTime } from '../../../utils';
import {
  ClientReviewStyled,
  ReviewHistoryContainerStyled,
  UploadedPDFViewWrapperStyled,
} from './style';
import UploadedPDFView from './UploadedPDFView';

const ClientReview = ({ review }: { review?: ClientReviewType }) => {
  if (!review) {
    return null;
  }
  const status = review.reviewStatus;
  return (
    <>
      <ClientReviewStyled>
        <Flex alignItems="center" gap="20px">
          <Typography variant="title" fontColor="primary" strong>
            Client Review
          </Typography>
          <Tag
            type={
              status === 'PENDING'
                ? 'warning'
                : status === 'ACCEPT'
                  ? 'success'
                  : status === 'REJECT'
                    ? 'danger'
                    : 'caution'
            }
          >
            {review.reviewStatus}
          </Tag>
        </Flex>
        {review?.reviewSendDate && (
          <InfoRowStyled>
            <Typography className="title">Sent to Client:</Typography>
            <Typography className="value">
              {humanReadableDateWithTime(review?.reviewSendDate)}
            </Typography>
          </InfoRowStyled>
        )}
        {review?.reviewDate && (
          <InfoRowStyled>
            <Typography className="title">Client Reviewed on:</Typography>
            <Typography className="value">
              {humanReadableDateWithTime(review?.reviewDate)}
            </Typography>
          </InfoRowStyled>
        )}
        <Typography strong>Review:</Typography>
        {review.reviews.map((clientReview) => (
          <Flex key={clientReview.substring(0, 50)} margin="0px 0px 10px">
            <Typography>{clientReview}</Typography>
          </Flex>
        ))}
      </ClientReviewStyled>
      <Typography
        variant="title"
        fontColor="primary"
        strong
        style={{ margin: '0px 0px 20px' }}
      >
        Escape Plan Review
      </Typography>
    </>
  );
};

type Props = {
  historyReview: ReviewType[];
  clientReview?: ClientReviewType[];
  isClient?: boolean;
};

const ReviewHistory = ({
  historyReview = [],
  clientReview = [],
  isClient = false,
}: Props) => {
  const theme = useTheme();

  const [showAllReviews, setShowAllReviews] = useState(false);

  const getClientReviewMappingWithAdminReview = (reviewId: string) => {
    const mappedClientReview = clientReview.find(
      (x) => x.adminReview === reviewId
    );
    return mappedClientReview;
  };

  if (historyReview.length === 0) {
    return null;
  }

  return (
    historyReview.length > 0 && (
      <>
        <Divider />
        <ReviewHistoryContainerStyled>
          <Typography
            fontColor="secondary"
            variant="title"
            strong
            style={{ margin: '30px 0px' }}
          >
            {isClient ? 'REVIEW' : 'REVIEW HISTORY'}
          </Typography>
          {Array.isArray(historyReview) &&
            historyReview.map((review, index) => {
              const isLast = index === historyReview.length - 1;

              if (!showAllReviews && index > 0) {
                return null;
              }
              return (
                <React.Fragment key={review._id}>
                  <ClientReview
                    review={getClientReviewMappingWithAdminReview(review._id)}
                  />

                  <UploadedPDFViewWrapperStyled
                    id={index === 0 ? 'first-review' : ''}
                  >
                    <Flex className="number-bullet-container" gap="20px">
                      <Typography className="number-bullet">
                        {index + 1}
                      </Typography>
                      {review?.reviewDate && (
                        <Typography strong>
                          Reviewed Date:{' '}
                          {humanReadableDateWithTime(review.reviewDate)}
                        </Typography>
                      )}
                    </Flex>
                    <UploadedPDFView review={review} />
                  </UploadedPDFViewWrapperStyled>
                  {!isLast && (
                    <Divider bgColor={theme.colors.TECHNICIAN.strong} />
                  )}
                </React.Fragment>
              );
            })}

          {historyReview.length > 1 && (
            <Button
              onClick={() => setShowAllReviews((prev) => !prev)}
              style={{ margin: '20px 0px' }}
            >
              {showAllReviews ? 'Show Less' : 'Show All'}
            </Button>
          )}
        </ReviewHistoryContainerStyled>
      </>
    )
  );
};

export default ReviewHistory;
